import React, {useCallback, useMemo, useState} from 'react';
import {useExternalUserList} from '../../../../../store/user';
import {ExUserRegistrationDetails} from 'hemwb-api';
import {tid} from '../../../../../testUtils';
import {useUserActionDialog} from '../common/UserActionDialog/useUserActionDialog';
import {useUserDetailSideBarModal} from '../common/UserActionMenu/useUserDetailSideBarModal';
import {useEnrollSideBarModal} from '../common/UserActionMenu/useEnrollSideBarModal';
import {Search} from '../../../../core/Search/Search';
import Header from '../spaceHeader/Header';
import DefaultPage from '../../../../layout/common/DefaultPage';
import AdminSpaceHeader from '../../common/AdminSpaceHeader/AdminSpaceHeader';
import AdminPage from '../../common/AdminPage/AdminPage';
import {useLocation} from 'react-router-dom';
import {Button, Spin, Table} from 'antd';
import {MoreOutlined} from '@ant-design/icons';
import type {ColumnsType, TablePaginationConfig} from 'antd/es/table';
import type {SorterResult} from 'antd/es/table/interface';
import StatusIndicator from '../common/StatusIndicator';
import UserRowActionMenu from '../common/UserRowActionMenu';

const ExternalUserList: React.FC = () => {
  const [selectedUserForDetail, setSelectedUserForDetail] = useState<ExUserRegistrationDetails | null>(null);
  const [selectedUserForEnroll, setSelectedUserForEnroll] = useState<ExUserRegistrationDetails | null>(null);
  const [searchValue, setSearchValue] = useState<string>('');
  const [pagination, setPagination] = useState<{current: number; pageSize: number}>({current: 1, pageSize: 10});
  const [sortBy, setSortBy] = useState<{field: string; order: 'ascend' | 'descend'} | null>({
    field: 'createdOn',
    order: 'descend',
  });
  const list = useExternalUserList();
  const {pathname} = useLocation();
  const adminExternalizationPage = pathname.startsWith('/admin/');
  const {openUserActionDialog, UserActionDialog} = useUserActionDialog();
  const {
    ModalComponent,
    openModal,
    closeModal: closeDetailModal,
  } = useUserDetailSideBarModal({
    selectedUser: selectedUserForDetail,
  });
  const {
    ModalComponent: EnrollModalComponent,
    openModal: openEnrollModal,
    closeModal: closeEnrollModal,
  } = useEnrollSideBarModal({
    selectedUser: selectedUserForEnroll,
  });

  // Filter and search users
  const filteredList = useMemo(() => {
    if (!list) return [];
    if (!searchValue) return list;
    const lcSearch = searchValue.toLocaleLowerCase();
    return list.filter(
      (user) =>
        (user.email || '').toLocaleLowerCase().includes(lcSearch) ||
        (user.lastName || '').toLocaleLowerCase().includes(lcSearch) ||
        (user.firstName || '').toLocaleLowerCase().includes(lcSearch),
    );
  }, [list, searchValue]);

  // Sort users
  const sortedList = useMemo(() => {
    if (!sortBy || !filteredList) return filteredList;
    const sorted = [...filteredList];
    sorted.sort((a, b) => {
      let aVal: any = a[sortBy.field as keyof ExUserRegistrationDetails] || '';
      let bVal: any = b[sortBy.field as keyof ExUserRegistrationDetails] || '';

      // Handle Date objects
      if (aVal instanceof Date && bVal instanceof Date) {
        return sortBy.order === 'ascend' ? aVal.getTime() - bVal.getTime() : bVal.getTime() - aVal.getTime();
      }

      // Handle string comparison
      if (typeof aVal === 'string') aVal = aVal.toLocaleLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLocaleLowerCase();
      return sortBy.order === 'ascend'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
    return sorted;
  }, [filteredList, sortBy]);

  const handleSearchChange = useCallback((search: string) => {
    setSearchValue(search);
    setPagination({current: 1, pageSize: 10}); // Reset to first page on search
  }, []);

  const handleTableChange = (
    pag: TablePaginationConfig,
    _: any,
    sorter: SorterResult<ExUserRegistrationDetails> | SorterResult<ExUserRegistrationDetails>[],
  ) => {
    if (pag.current) setPagination({current: pag.current, pageSize: pag.pageSize || 10});
    if (!Array.isArray(sorter) && sorter.field) {
      setSortBy({
        field: sorter.field as string,
        order: (sorter.order as 'ascend' | 'descend') || 'ascend',
      });
    } else {
      setSortBy(null);
    }
  };

  const antdColumns: ColumnsType<ExUserRegistrationDetails> = useMemo(
    () => [
      {
        title: 'First Name',
        dataIndex: 'firstName',
        key: 'firstName',
        sorter: true,
        width: '20%',
      },
      {
        title: 'Last Name',
        dataIndex: 'lastName',
        key: 'lastName',
        sorter: true,
        width: '20%',
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
        sorter: true,
        width: '30%',
      },
      {
        title: 'Created On',
        dataIndex: 'createdOn',
        key: 'createdOn',
        sorter: true,
        width: '15%',
        render: (createdOn) =>
          createdOn instanceof Date
            ? createdOn.toLocaleDateString('en-US', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })
            : '-',
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        sorter: true,
        width: '15%',
        render: (_, record) => <StatusIndicator status={record.status} />,
      },
      {
        title: '',
        key: 'actions',
        width: '15%',
        align: 'right' as const,
        render: (_, record) => (
          <UserRowActionMenu
            user={record}
            adminExternalizationPage={adminExternalizationPage}
            onOpenDialog={(user: ExUserRegistrationDetails, action: string) => {
              openUserActionDialog({user, action});
            }}
            onOpenModal={(event) => {
              closeEnrollModal();
              setSelectedUserForEnroll(null);
              setSelectedUserForDetail(record);
              openModal(event, record);
            }}
            onOpenEnrollModal={(user: ExUserRegistrationDetails, event: React.MouseEvent<HTMLElement>) => {
              closeDetailModal();
              setSelectedUserForDetail(null);
              setSelectedUserForEnroll(user);
              openEnrollModal(event);
            }}
          >
            <Button type="text" icon={<MoreOutlined />} {...tid('btn', 'row-actions', String(record.id))} />
          </UserRowActionMenu>
        ),
      },
    ],
    [adminExternalizationPage, openUserActionDialog, openEnrollModal, closeDetailModal, closeEnrollModal],
  );

  const isLoading = !list;

  return (
    <>
      {!adminExternalizationPage ? (
        <DefaultPage>
          <div style={{padding: '0px 20px 0px 20px', background: '#fff'}}>
            <Header
              Search={<Search value={searchValue} onValueChange={handleSearchChange} />}
              ActionButton={
                <Button
                  type="primary"
                  style={{padding: '18px'}}
                  onClick={(e) => {
                    closeDetailModal();
                    closeEnrollModal();
                    setSelectedUserForDetail(null);
                    setSelectedUserForEnroll(null);
                    openEnrollModal(e);
                  }}
                  {...tid('btn', 'enroll-user')}
                >
                  Register New User
                </Button>
              }
            />

            <Spin spinning={isLoading}>
              <Table<ExUserRegistrationDetails>
                columns={antdColumns}
                dataSource={sortedList}
                rowKey={(record) => record.id || ''}
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: sortedList?.length || 0,
                  showSizeChanger: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                }}
                onChange={handleTableChange}
                locale={{emptyText: 'No users were found'}}
                {...tid('externalization-user-table')}
              />
            </Spin>
          </div>
        </DefaultPage>
      ) : (
        <AdminPage style={{paddingLeft: '2rem'}} header={<AdminSpaceHeader label="Activate and deactivate users" />}>
          <div style={{padding: '10px'}}>
            <Search value={searchValue} onValueChange={handleSearchChange} />
          </div>
          <Spin spinning={isLoading}>
            <Table<ExUserRegistrationDetails>
              columns={antdColumns}
              dataSource={sortedList}
              rowKey={(record) => record.id || ''}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: sortedList?.length || 0,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              }}
              onChange={handleTableChange}
              locale={{emptyText: 'No users were found'}}
              {...tid('externalization-user-table')}
            />
          </Spin>
        </AdminPage>
      )}
      {ModalComponent}
      {EnrollModalComponent}
      {UserActionDialog}
    </>
  );
};

export default ExternalUserList;
